import React, { useLayoutEffect, useRef } from 'react';
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import * as am5percent from "@amcharts/amcharts5/percent";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

/** El asistente manda { type, data } crudo (sin "keys" explícitos) — detectamos aquí el
 * primer campo string como categoría y el primer campo numérico como valor. */
function deriveKeys(rows) {
  const sample = rows[0] || {};
  const keys = Object.keys(sample);
  const category = keys.find((k) => typeof sample[k] === 'string') || keys[0];
  const value = keys.find((k) => typeof sample[k] === 'number' && k !== category) || keys[1] || keys[0];
  return { category, value };
}

const ChartRenderer = ({ config }) => {
  const chartRef = useRef(null);

  useLayoutEffect(() => {
    if (!config?.data?.length) return undefined;

    let root = am5.Root.new(chartRef.current);
    root.setThemes([am5themes_Animated.new(root)]);

    const { type, data } = config;
    const { category, value } = deriveKeys(data);

    if (type === 'bar' || type === 'column' || type === 'line') {
      let chart = root.container.children.push(am5xy.XYChart.new(root, {
        panX: true,
        panY: true,
        wheelX: "panX",
        wheelY: "zoomX",
        pinchZoomX: true
      }));

      let cursor = chart.set("cursor", am5xy.XYCursor.new(root, {}));
      cursor.lineY.set("visible", false);

      let xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 30 });
      xRenderer.labels.template.setAll({ rotation: -45, centerY: am5.p50, centerX: am5.p100 });

      let xAxis = chart.xAxes.push(am5xy.CategoryAxis.new(root, {
        maxDeviation: 0.3,
        categoryField: category,
        renderer: xRenderer,
        tooltip: am5.Tooltip.new(root, {})
      }));

      let yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
        maxDeviation: 0.3,
        renderer: am5xy.AxisRendererY.new(root, {})
      }));

      if (type === 'line') {
        let series = chart.series.push(am5xy.LineSeries.new(root, {
          name: "Series 1",
          xAxis, yAxis,
          valueYField: value,
          categoryXField: category,
          tooltip: am5.Tooltip.new(root, { labelText: "{valueY}" })
        }));
        xAxis.data.setAll(data);
        series.data.setAll(data);
        series.appear(1000);
      } else {
        let series = chart.series.push(am5xy.ColumnSeries.new(root, {
          name: "Series 1",
          xAxis, yAxis,
          valueYField: value,
          sequencedInterpolation: true,
          categoryXField: category,
          tooltip: am5.Tooltip.new(root, { labelText: "{valueY}" })
        }));

        series.columns.template.setAll({ cornerRadiusTL: 5, cornerRadiusTR: 5 });
        series.columns.template.adapters.add("fill", function (fill, target) {
          return chart.get("colors").getIndex(series.columns.indexOf(target));
        });

        xAxis.data.setAll(data);
        series.data.setAll(data);
        series.appear(1000);
      }
      chart.appear(1000, 100);
    } else if (type === 'pie') {
      let chart = root.container.children.push(am5percent.PieChart.new(root, {
        layout: root.verticalLayout
      }));

      let series = chart.series.push(am5percent.PieSeries.new(root, {
        valueField: value,
        categoryField: category
      }));

      series.data.setAll(data);
      series.appear(1000, 100);
    }

    return () => {
      root.dispose();
    };
  }, [config]);

  if (!config?.data?.length) return null;

  return (
    <div style={{ width: "100%", height: "260px", marginTop: "10px", marginBottom: "10px" }}>
      <div ref={chartRef} style={{ width: "100%", height: "100%" }}></div>
    </div>
  );
};

export default ChartRenderer;
